import { useQuery, useMutation, useQueryClient } from '../useQuery'
import { queryKeys } from '@/api/queryKeys'
import * as inventoryApi from '@/api/inventory'
import type {
  CreateInventoryItemRequest,
  AdjustInventoryRequest,
  InventoryPageParams,
} from '@/api/types/inventory'

export function useInventoryItems() {
  return useQuery({
    queryKey: queryKeys.inventoryItems,
    queryFn: inventoryApi.getInventoryItems,
  })
}

export function useInventoryItemPage(params: InventoryPageParams = {}) {
  return useQuery({
    queryKey: [...queryKeys.inventoryItems, params],
    queryFn: () => inventoryApi.getInventoryItemPage(params),
  })
}

export function useInventoryItem(skuCode: string) {
  return useQuery({
    queryKey: queryKeys.inventoryItem(skuCode),
    queryFn: () => inventoryApi.getInventoryItem(skuCode),
    enabled: !!skuCode,
  })
}

export function useInventoryLedger(skuCode: string) {
  return useQuery({
    queryKey: queryKeys.inventoryLedger(skuCode),
    queryFn: () => inventoryApi.getInventoryLedger(skuCode),
    enabled: !!skuCode,
  })
}

export function useLowStockAlerts() {
  const { data: items } = useInventoryItems()
  const alerts = items ? inventoryApi.calculateLowStockAlerts(items) : []
  return {
    alerts,
    count: alerts.length,
    criticalCount: alerts.filter((item) => item.level === 'critical').length,
    warningCount: alerts.filter((item) => item.level === 'warning').length,
  }
}

export function useCreateInventoryItem() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInventoryItemRequest) => inventoryApi.createInventoryItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems })
    },
  })
}

export function useAdjustInventory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ skuCode, data }: { skuCode: string; data: AdjustInventoryRequest }) =>
      inventoryApi.adjustInventory(skuCode, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItems })
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryItem(variables.skuCode) })
      queryClient.invalidateQueries({ queryKey: queryKeys.inventoryLedger(variables.skuCode) })
    },
  })
}
